import React from "react";
import PageHeader from "../components/PageHeader";
import ImageTile from "../components/ImageTile";
import { aboutImages, heroImages } from "../data/images";

const values = [
  { icon: "🌱", title: "Direct-from-Farm Procurement", body: "We source directly from farmers we know personally — cutting out unnecessary middlemen and ensuring traceability from seed to shipment." },
  { icon: "💸", title: "Competitive Pricing",          body: "By streamlining the supply chain and eliminating redundant intermediaries, we offer premium-quality produce at highly competitive export rates." },
  { icon: "🔬", title: "Quality Assurance",            body: "Our on-site presence for energy installations gives us first-hand knowledge of soil conditions, water quality and cultivation practices on every partner farm." },
  { icon: "🚛", title: "Timely Delivery",              body: "Our established rural logistics network ensures produce moves from farm to port with maximum efficiency and minimal post-harvest loss." },
];

const timeline = [
  { year: "Solar Installation",   title: "Where the bond begins",  body: "Years of installing solar water pumps gave us deep, trust-based relationships with thousands of farmers." },
  { year: "Farm-Gate Access",     title: "Direct procurement",      body: "We source straight from partner farms — full visibility into cultivation, water quality and harvest timing." },
  { year: "Aggregation & Grading", title: "Pack-house ready",        body: "Sorting, grading, hot-water treatment (for mango) and export-grade packing at certified facilities." },
  { year: "Global Export",        title: "Farm to your port",       body: "FSSAI, APEDA, phytosanitary docs and reefer logistics — ready for over 30 countries." },
];

const stats = [
  { v: "1000+", l: "Partner Farms" },
  { v: "360°",  l: "Supply Chain Control" },
  { v: "100%",  l: "Traceable Origin" },
  { v: "24/7",  l: "Buyer Support" },
];

const aboutStats = [
  { v: "1000+", l: "Partner Farms",        icon: "🌾" },
  { v: "30+",   l: "Export Countries",     icon: "🌍" },
  { v: "100%",  l: "Traceable Origin",     icon: "🔬" },
  { v: "24/7",  l: "Buyer Support",        icon: "🤝" },
];

export default function AboutPage() {
  return (
    <>
      <PageHeader eyebrow="About Myriad Green Energy"
        title="Synergizing renewable energy"
        titleAccent="& global agriculture."
        subtitle="Headquartered in Pune, Maharashtra, Myriad Green Energy is a diversified merchant exporter — a unique edge built on our roots in solar energy, bridging Indian farms with the global market."
        image={aboutImages.fieldWide} emoji="🌾"
        rightContent={
          <div className="glass-strong shimmer-border rounded-3xl p-6 w-full max-w-sm">
            <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 mb-5">At a Glance</div>
            <div className="grid grid-cols-2 gap-3">
              {aboutStats.map(s => (
                <div key={s.l} className="glass rounded-2xl p-4 text-center">
                  <div className="text-2xl mb-1">{s.icon}</div>
                  <div className="font-display text-2xl font-semibold text-gradient">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        } />

      {/* Story */}
      <section className="relative py-24 md:py-32">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                <ImageTile src={aboutImages.farmer} alt="Farmer" emoji="🌾"
                  className="aspect-[3/4] rounded-3xl overflow-hidden"
                  imgClassName="hover:scale-110 transition-transform duration-700" />
                <div className="flex flex-col gap-3">
                  <ImageTile src={heroImages.harvestHands} alt="Harvest" emoji="🥭"
                    className="aspect-square rounded-3xl overflow-hidden"
                    imgClassName="hover:scale-110 transition-transform duration-700" />
                  <ImageTile src={aboutImages.warehouse} alt="Warehouse" emoji="📦"
                    className="aspect-square rounded-3xl overflow-hidden"
                    imgClassName="hover:scale-110 transition-transform duration-700" />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 glass-pill rounded-full px-4 py-1.5 mb-5">
                <span className="text-[11px] uppercase tracking-[0.22em] text-stone-500/85">The Green Engine</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
                From solar power to <span className="text-gradient">farm power.</span>
              </h2>
              <p className="mt-6 text-stone-600 text-lg leading-relaxed">
                Our strength lies in a deep-rooted presence across India's rural landscape. As an established solar installation company, we have spent years building direct, trust-based relationships with thousands of farmers during the installation of solar water pumps.
              </p>
              <p className="mt-4 text-stone-500 leading-relaxed">
                This pre-existing bond is our most powerful competitive advantage — it transforms us from a typical trader into a genuine farm-gate partner with unmatched access, transparency and reliability. We specialise in the global distribution of fresh vegetables, premium fruits and processed agricultural products, leveraging a vertically integrated infrastructure that sets us apart.
              </p>

              <div className="mt-10 grid sm:grid-cols-2 gap-4">
                {values.map(v => (
                  <div key={v.title} className="glass rounded-2xl p-6 glass-hover">
                    <div className="text-3xl mb-3">{v.icon}</div>
                    <div className="font-display text-lg font-medium text-stone-700">{v.title}</div>
                    <p className="text-sm text-stone-400 mt-2 leading-relaxed">{v.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-20 md:py-28">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 glass-pill rounded-full px-4 py-1.5 mb-5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-stone-500/85">Farm to Port</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
              One supply chain, <span className="text-gradient">four careful steps.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {timeline.map((t, i) => (
              <div key={t.title} className="relative glass rounded-3xl p-7 glass-hover fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-200 to-amber-500 text-amber-950 flex items-center justify-center font-semibold">
                    {i + 1}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400">{t.year}</div>
                </div>
                <h3 className="font-display text-xl font-medium text-stone-600">{t.title}</h3>
                <p className="text-sm text-stone-400 mt-2 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why buyers choose us */}
      <section className="relative py-20 md:py-28">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 glass-pill rounded-full px-4 py-1.5 mb-5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-stone-500/85">Why International Buyers Choose Myriad</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
              Reliability, transparency, <span className="text-gradient">long-term partnership.</span>
            </h2>
            <p className="mt-5 text-stone-600 leading-relaxed">
              For importers, wholesalers and distributors, choosing the right sourcing partner is about more than price. We deliver on all three.
            </p>
          </div>

          <div className="glass-strong shimmer-border noise rounded-3xl p-6 md:p-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <div key={s.l} className={`text-center ${i !== 0 ? "md:border-l md:border-white/10" : ""} px-2`}>
                <div className="font-display text-4xl md:text-5xl font-semibold text-gradient">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mt-1.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
